import { useState, useEffect } from 'react';
import { router } from '@inertiajs/react';
import { Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface ScheduleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newsletterId: number;
  issueId: number;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';
  scheduledAt: string | null;
  editable: boolean;
}

export default function ScheduleModal({
  open,
  onOpenChange,
  newsletterId,
  issueId,
  status,
  scheduledAt,
  editable,
}: ScheduleModalProps) {
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  // Initialize date/time from scheduledAt when modal opens
  useEffect(() => {
    if (open && scheduledAt) {
      const date = new Date(scheduledAt);
      setScheduleDate(date.toISOString().split('T')[0]);
      setScheduleTime(date.toTimeString().slice(0, 5));
    } else if (open) {
      // Default to tomorrow at 9am
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      setScheduleDate(tomorrow.toISOString().split('T')[0]);
      setScheduleTime('09:00');
    }
  }, [open, scheduledAt]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleSchedule = (e: React.FormEvent) => {
    e.preventDefault();
    const scheduledAtDate = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
    router.post(`/admin/newsletters/${newsletterId}/issues/${issueId}/schedule`, {
      newsletter_issue: { scheduled_at: scheduledAtDate },
    });
    onOpenChange(false);
  };

  const handleUnschedule = () => {
    if (confirm('Are you sure you want to unschedule this issue? It will be moved back to draft status.')) {
      router.post(`/admin/newsletters/${newsletterId}/issues/${issueId}/unschedule`);
      onOpenChange(false);
    }
  };

  const isScheduled = status === 'scheduled';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Schedule Issue</DialogTitle>
          <DialogDescription>
            {isScheduled
              ? 'Reschedule this newsletter to a new time.'
              : 'Choose when you want to send this newsletter to your subscribers.'}
          </DialogDescription>
        </DialogHeader>
        
        {isScheduled && editable && (
          <div className="mb-4 p-3 bg-muted rounded-lg flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Currently scheduled for: <strong>{formatDate(scheduledAt)}</strong>
            </p>
            <button
              type="button"
              onClick={handleUnschedule}
              className="p-2 bg-red-600 text-white rounded hover:bg-red-700"
              title="Unschedule"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
        
        <form onSubmit={handleSchedule}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="schedule-date" className="text-sm font-medium">
                Date
              </label>
              <input
                id="schedule-date"
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                required
              />
            </div>
            
            <div className="grid gap-2">
              <label htmlFor="schedule-time" className="text-sm font-medium">
                Time
              </label>
              <input
                id="schedule-time"
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                required
              />
            </div>
          </div>
          
          <DialogFooter>
            <button
              type="submit"
              className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
            >
              {isScheduled ? 'Reschedule' : 'Schedule'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
